import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Home, Info } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const australianStates = [
  { value: 'QLD', label: 'Queensland' },
  { value: 'NSW', label: 'New South Wales' },
  { value: 'VIC', label: 'Victoria' },
  { value: 'SA', label: 'South Australia' },
  { value: 'WA', label: 'Western Australia' },
  { value: 'TAS', label: 'Tasmania' },
  { value: 'NT', label: 'Northern Territory' },
  { value: 'ACT', label: 'Australian Capital Territory' },
];

export default function PackageEnquiryModal({ isOpen, onClose, selectedPackage }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [packageChoice, setPackageChoice] = useState('');
  
  const [formData, setFormData] = useState({
    fullName: '',
    address: '',
    suburb: '',
    state: 'NSW',
    postcode: '',
    email: '',
    phone: '',
    description: '',
  });

  useEffect(() => {
    if (selectedPackage) {
      setPackageChoice(selectedPackage);
    }
  }, [selectedPackage]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setPackageChoice('');
    setFormData({
      fullName: '',
      address: '',
      suburb: '',
      state: 'NSW',
      postcode: '',
      email: '',
      phone: '',
      description: '',
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!packageChoice) {
      toast.error('Please select a package');
      return;
    }

    if (!formData.fullName || !formData.address || !formData.suburb || !formData.postcode || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const description = `PACKAGE ENQUIRY: ${packageChoice}\n\n${formData.description || 'No additional details provided'}`;

      const payload = {
        full_name: formData.fullName,
        address: formData.address,
        suburb: formData.suburb,
        state: formData.state,
        postcode: formData.postcode,
        email: formData.email,
        phone: formData.phone,
        service_type: packageChoice,
        description: description,
        use_calculator: false,
        calculated_quote: null,
        input_value: null,
        input_value_2: null,
      };

      const response = await axios.post(`${API_URL}/api/quote`, payload);
      
      if (response.data.success) {
        toast.success('Thanks! We\'ll be in touch shortly to arrange your property assessment.');
        handleClose();
      }
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit enquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="package-enquiry-modal">
        <DialogHeader>
          <DialogTitle className="font-oswald text-2xl text-green-900 flex items-center">
            <Home className="mr-3 text-amber-600" size={28} />
            Book a Property Assessment
          </DialogTitle>
          <DialogDescription className="sr-only">
            Enquire about a service package from Empire Lawn Care and Maintenance
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Info Banner */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start">
            <Info className="text-amber-600 mr-3 flex-shrink-0 mt-0.5" size={20} />
            <p className="font-inter text-sm text-amber-800">
              We'll arrange a time to visit your property, assess your requirements, and provide a customised quote.
            </p>
          </div>

          {/* Package Selection */}
          <div className="space-y-2">
            <Label className="font-inter font-medium text-gray-700">
              Select Package <span className="text-red-500">*</span>
            </Label>
            <Select value={packageChoice} onValueChange={setPackageChoice}>
              <SelectTrigger data-testid="package-select">
                <SelectValue placeholder="Choose a package..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Lawn Care Package">Lawn Care Package</SelectItem>
                <SelectItem value="Ultimate Property Package">Ultimate Property Package</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Personal Details */}
          <div className="space-y-4">
            <h3 className="font-oswald text-lg font-semibold text-gray-900 border-b pb-2">
              Your Details
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="font-inter text-sm text-gray-600">
                  Full Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  required
                  className="mt-1"
                  data-testid="input-full-name"
                />
              </div>

              <div>
                <Label className="font-inter text-sm text-gray-600">
                  Phone <span className="text-red-500">*</span>
                </Label>
                <Input
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Enter your phone number"
                  required
                  className="mt-1"
                  data-testid="input-phone"
                />
              </div>
            </div>

            <div>
              <Label className="font-inter text-sm text-gray-600">
                Email <span className="text-red-500">*</span>
              </Label>
              <Input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email address"
                required
                className="mt-1"
                data-testid="input-email"
              />
            </div>

            <div>
              <Label className="font-inter text-sm text-gray-600">
                Street Address <span className="text-red-500">*</span>
              </Label>
              <Input
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Enter your street address"
                required
                className="mt-1"
                data-testid="input-address"
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="col-span-2 md:col-span-1">
                <Label className="font-inter text-sm text-gray-600">
                  Suburb <span className="text-red-500">*</span>
                </Label>
                <Input
                  name="suburb"
                  value={formData.suburb}
                  onChange={handleInputChange}
                  placeholder="Suburb"
                  required
                  className="mt-1"
                  data-testid="input-suburb"
                />
              </div>

              <div>
                <Label className="font-inter text-sm text-gray-600">
                  State <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.state}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, state: value }))}
                >
                  <SelectTrigger className="mt-1" data-testid="select-state">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {australianStates.map(state => (
                      <SelectItem key={state.value} value={state.value}>
                        {state.value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="font-inter text-sm text-gray-600">
                  Postcode <span className="text-red-500">*</span>
                </Label>
                <Input
                  name="postcode"
                  value={formData.postcode}
                  onChange={handleInputChange}
                  placeholder="Postcode"
                  required
                  className="mt-1"
                  data-testid="input-postcode"
                />
              </div>
            </div>

            <div>
              <Label className="font-inter text-sm text-gray-600">
                Property Details / Additional Requirements
              </Label>
              <Textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Tell us about your property size, any specific requirements, or add-on services you're interested in..."
                rows={4}
                className="mt-1"
                data-testid="input-description"
              />
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1 font-oswald"
              data-testid="cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !packageChoice}
              className="flex-1 bg-green-900 hover:bg-green-800 text-white font-oswald btn-empire"
              data-testid="submit-enquiry-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Book Assessment'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
